import type { ReactElement } from 'react';

import useHasUnreadWhatsNew from '../hooks/useHasUnreadWhatsNew';
import useNewlyIncludedPages from '../hooks/useNewlyIncludedPages';
import usePlusLock from '../hooks/usePlusLock';
import type { SidebarNavItem } from '../types';

import SidebarAddOnTag from './SidebarAddOnTag';
import SidebarCountBadge from './SidebarCountBadge';
import SidebarDot from './SidebarDot';
import SidebarNewTag from './SidebarNewTag';
import SidebarPlusTag from './SidebarPlusTag';

/**
 * The one marker at the end of a sidebar item: a count, a Plus+ tag, the green dot (unread What's
 * New, or a page the new pricing added to the plan), "Add-on" or "New".
 */
export default function SidebarItemTag({ item }: { item: SidebarNavItem }): ReactElement | null {
  const plusLock = usePlusLock(item.plusFeature);
  const hasUnreadWhatsNew = useHasUnreadWhatsNew(Boolean(item.unreadDot));
  const { isNewlyIncluded } = useNewlyIncludedPages();

  if (item.badge && !isNewPlaceholder(item)) {
    return <SidebarCountBadge count={item.badge.title} colour={item.countColour} />;
  }
  if (plusLock === 'pending') return null;
  if (plusLock === 'locked') return <SidebarPlusTag />;
  if (hasUnreadWhatsNew) return <SidebarDot label="New announcements" />;
  if (isNewlyIncluded(item.id)) return <SidebarDot label="Now included in your plan" />;
  if (item.addOn) return <SidebarAddOnTag />;
  if (item.new) return <SidebarNewTag />;
  return null;
}

// `navigationSlice` gives a new page a "New" badge while it has no count; the sidebar shows that as
// the "New" tag, not as a count.
function isNewPlaceholder(item: SidebarNavItem): boolean {
  return Boolean(item.new) && item.badge?.title === 'New';
}
